/**
 * @file     noticeStyles.ts
 * @purpose  Shared styles for the site notice and the cookie preferences
 *           panel: one neutral button used for both Decline and Accept so
 *           they carry identical weight, a quiet text link, a primary button
 *           for Save, and the single soft shadow and entrance motion.
 */
import { alpha, keyframes, type SxProps, type Theme } from '@mui/material/styles';
import type { SemanticColorTokens } from '@/core/types';

const focusRing = (c: SemanticColorTokens) => ({
  outline: `2px solid ${c.interactiveFocusRing}`,
  outlineOffset: 2,
});

export function neutralButtonSx(c: SemanticColorTokens, radiusPx: number): SxProps<Theme> {
  return {
    minHeight: { xs: 44, md: 32 },
    px: 4,
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    borderRadius: `${radiusPx}px`,
    color: c.textPrimary,
    bgcolor: c.surfaceCard,
    border: '1px solid',
    borderColor: c.borderStrong,
    cursor: 'pointer',
    transition: 'background-color 150ms ease, border-color 150ms ease',
    '@media (hover: hover)': { '&:hover': { bgcolor: c.interactiveHover } },
    '&:active': { bgcolor: c.interactivePressed },
    '&:focus-visible': focusRing(c),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  };
}

export function primaryButtonSx(c: SemanticColorTokens, radiusPx: number): SxProps<Theme> {
  return {
    minHeight: { xs: 44, sm: 36 },
    px: 5,
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    borderRadius: `${radiusPx}px`,
    color: c.textOnPrimary,
    bgcolor: c.textPrimary,
    border: '1px solid',
    borderColor: c.textPrimary,
    cursor: 'pointer',
    transition: 'opacity 150ms ease',
    '@media (hover: hover)': { '&:hover': { opacity: 0.88 } },
    '&:focus-visible': focusRing(c),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  };
}

export function quietLinkSx(c: SemanticColorTokens, radiusPx: number): SxProps<Theme> {
  return {
    minHeight: { xs: 44, md: 32 },
    px: 1,
    fontSize: 13.5,
    fontWeight: 500,
    color: c.textSecondary,
    borderRadius: `${radiusPx}px`,
    cursor: 'pointer',
    transition: 'color 150ms ease',
    '@media (hover: hover)': { '&:hover': { color: c.textPrimary, textDecoration: 'underline', textUnderlineOffset: '3px' } },
    '&:focus-visible': focusRing(c),
  };
}

export function inlineLinkSx(c: SemanticColorTokens): SxProps<Theme> {
  return {
    color: c.interactivePrimary,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
    textDecorationThickness: '1px',
    '&:hover': { textDecorationThickness: '2px' },
    '&:focus-visible': { ...focusRing(c), borderRadius: '2px' },
  };
}

export function noticeShadow(theme: Theme): string {
  return `0 4px 16px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.36 : 0.06)}`;
}

export const noticeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;
